import { ZodError, type ZodType } from 'zod';

import { getConfig } from '@/config';
import { parseTolerant } from '@/api/tolerant';
import { recordDrift } from '@/lib/drift';

/**
 * A failed API call, carrying enough to render an honest error state.
 *
 * The read API's envelope is `{"error": "<Prefix>: <message>"}` and the ingest
 * plane's is `{code, message}`. Both are parsed here, and neither is trusted to
 * exist: a CloudFront 502 has no JSON body at all.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly requestId: string | null;

  constructor(
    message: string,
    options: { status: number; code?: string | null; requestId?: string | null },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code ?? null;
    this.requestId = options.requestId ?? null;
  }

  /** 401 and 403 mean re-authenticate or ask an admin, never "retry". */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** Plan 4.4: another tenant's resource and a missing one are both 404. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

/** The response parsed but did not match the shape this app was built against. */
export class ContractError extends Error {
  readonly path: string;

  constructor(path: string, detail: string) {
    super(`The API returned an unexpected shape for ${path}: ${detail}`);
    this.name = 'ContractError';
    this.path = path;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

/** Supplies the bearer credential. Wired to the auth provider in `queries.ts`. */
export type TokenProvider = () => Promise<string | null>;

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(`${getConfig().apiBaseUrl}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function messageFromBody(body: unknown, status: number): { message: string; code: string | null } {
  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>;
    if (typeof record['error'] === 'string') {
      return { message: record['error'], code: null };
    }
    if (typeof record['message'] === 'string') {
      return {
        message: record['message'],
        code: typeof record['code'] === 'string' ? record['code'] : null,
      };
    }
    if (typeof record['code'] === 'string') {
      return { message: record['code'], code: record['code'] };
    }
  }
  return { message: `The request failed with status ${status}.`, code: null };
}

/**
 * One request, validated on the way out.
 *
 * Validation is not paranoia about our own backend: it is what turns a field
 * rename into a named error state on one screen instead of a blank page from an
 * undefined property read three components deep. Zod object schemas strip
 * unknown keys rather than rejecting them, so the API can add fields freely.
 */
export async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  getToken: TokenProvider,
  options: RequestOptions = {},
): Promise<T> {
  const token = await getToken();
  if (token === null) {
    throw new ApiError('Your session has ended. Sign in again to continue.', {
      status: 401,
    });
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
  const method = options.method ?? 'GET';
  let payload: string | undefined;
  if (options.body !== undefined) {
    payload = JSON.stringify(options.body);
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body: payload,
      signal: options.signal,
      // No cookies are involved anywhere: the credential is the bearer token.
      credentials: 'omit',
      mode: 'cors',
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // Status 0 means the request never produced a response: offline, DNS,
    // TLS, or a corporate proxy. Saying so beats "something went wrong".
    throw new ApiError(
      'The dashboard could not reach the Zeligate API. Check your connection and try again.',
      { status: 0 },
    );
  }

  const requestId = response.headers.get('x-amzn-requestid');

  if (response.status === 204) {
    const parsed = schema.safeParse(null);
    if (!parsed.success) throw new ContractError(path, 'expected a body but got 204');
    return parsed.data;
  }

  const text = await response.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const { message, code } = messageFromBody(body, response.status);
    throw new ApiError(message, { status: response.status, code, requestId });
  }

  // Strict about the envelope, tolerant about the rows. A single malformed row
  // used to reject the whole response and blank the screen; it now costs that
  // row, and the drop is recorded where it can be seen. Anything wrong outside
  // an array element still throws, because a screen that cannot tell what it is
  // looking at should say so rather than draw a confident empty state.
  try {
    const { data, dropped } = parseTolerant(schema, body);
    recordDrift(path, dropped);
    return data;
  } catch (error) {
    if (!(error instanceof ZodError)) throw error;
    const first = error.issues[0];
    const detail = first
      ? `${first.path.join('.') || '<root>'} ${first.message}`
      : 'no detail available';
    throw new ContractError(path, detail);
  }
}
